import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import ApiService from '@/services/api';
import { logger } from '@/utils/logger';
import {
  IOnlineUser,
  ISignalMessage,
  IStoredVoiceMessage,
  IVoiceMessage,
  ConnectionState,
  RTC_CONFIG,
} from '@/types/voicemessaging';

export interface WebRTCContextType {
  // Connection state
  isConnected: boolean;
  isEnabled: boolean; // User preference (persisted)
  connectionState: ConnectionState;
  error: string | null;

  // Online users
  onlineUsers: IOnlineUser[];

  // Pending messages (offline delivery)
  pendingMessages: IStoredVoiceMessage[];
  pendingCount: number;
  unplayedCount: number;
  refreshPendingMessages: () => Promise<void>;

  // Voice messaging
  sendVoiceMessage: (to: string, audioBlob: Blob, duration: number) => Promise<boolean>;
  onVoiceMessage: (callback: (msg: IVoiceMessage) => void) => () => void;

  // Message management
  deleteMessage: (messageId: string) => Promise<void>;
  markMessagePlayed: (messageId: string) => Promise<void>;

  // Connection controls
  connect: () => void;
  disconnect: () => void;
}

export const WebRTCContext = createContext<WebRTCContextType | undefined>(undefined);

interface WebRTCProviderProps {
  children: ReactNode;
}

export const WebRTCProvider: React.FC<WebRTCProviderProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  // Manual enable/disable state (persisted in localStorage)
  const [isEnabled, setIsEnabled] = useState<boolean>(() => {
    const stored = localStorage.getItem('webrtc_enabled');
    return stored === 'true'; // Default to disabled
  });

  // WebSocket state
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [error, setError] = useState<string | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const manualDisconnectRef = useRef(false); // Track if user manually disconnected

  // Online users
  const [onlineUsers, setOnlineUsers] = useState<IOnlineUser[]>([]);

  // Pending messages
  const [pendingMessages, setPendingMessages] = useState<IStoredVoiceMessage[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [unplayedCount, setUnplayedCount] = useState(0);

  // Voice message callbacks
  const voiceMessageCallbacksRef = useRef<Set<(msg: IVoiceMessage) => void>>(new Set());

  // Peer connections for P2P messaging
  const peerConnectionsRef = useRef<Map<string, RTCPeerConnection>>(new Map());
  const dataChannelsRef = useRef<Map<string, RTCDataChannel>>(new Map());

  // Convert audio blob to base64
  const blobToBase64 = useCallback((blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        // Remove data URL prefix (e.g., "data:audio/webm;base64,")
        const base64Data = base64.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }, []);

  // Refresh pending messages from server
  const refreshPendingMessages = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const [messages, count] = await Promise.all([
        ApiService.getVoiceMessages(),
        ApiService.getVoiceMessageCount(),
      ]);
      setPendingMessages(messages);
      setPendingCount(count.total);
      setUnplayedCount(count.unplayed);
    } catch (error) {
      logger.error('Failed to fetch pending messages:', error);
    }
  }, [isAuthenticated]);

  // Send signal message via WebSocket
  const sendSignal = useCallback((message: Partial<ISignalMessage>) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  // Create peer connection for a user
  const createPeerConnection = useCallback(
    (targetUsername: string): RTCPeerConnection => {
      const pc = new RTCPeerConnection(RTC_CONFIG);

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          sendSignal({
            type: 'ice-candidate',
            to: targetUsername,
            payload: JSON.stringify(event.candidate),
          });
        }
      };

      pc.ondatachannel = (event) => {
        const channel = event.channel;
        dataChannelsRef.current.set(targetUsername, channel);

        channel.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data) as IVoiceMessage;
            voiceMessageCallbacksRef.current.forEach((cb) => cb(msg));
          } catch (err) {
            logger.error('Failed to parse voice message:', err);
          }
        };
      };

      peerConnectionsRef.current.set(targetUsername, pc);
      return pc;
    },
    [sendSignal]
  );

  // Handle incoming signal messages
  const handleSignalMessage = useCallback(
    async (message: ISignalMessage) => {
      switch (message.type) {
        case 'online-users': {
          try {
            const users = JSON.parse(message.payload) as IOnlineUser[];
            setOnlineUsers(users.filter((u) => u.username !== user?.username));
          } catch (err) {
            logger.error('Failed to parse online users:', err);
          }
          break;
        }

        case 'offer': {
          let pc = peerConnectionsRef.current.get(message.from);
          if (!pc) {
            pc = createPeerConnection(message.from);
          }

          try {
            const offer = JSON.parse(message.payload);
            await pc.setRemoteDescription(new RTCSessionDescription(offer));
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);

            sendSignal({
              type: 'answer',
              to: message.from,
              payload: JSON.stringify(answer),
            });
          } catch (err) {
            logger.error('Failed to handle offer:', err);
          }
          break;
        }

        case 'answer': {
          const pc = peerConnectionsRef.current.get(message.from);
          if (pc) {
            try {
              const answer = JSON.parse(message.payload);
              await pc.setRemoteDescription(new RTCSessionDescription(answer));
            } catch (err) {
              logger.error('Failed to handle answer:', err);
            }
          }
          break;
        }

        case 'ice-candidate': {
          const pc = peerConnectionsRef.current.get(message.from);
          if (pc) {
            try {
              const candidate = JSON.parse(message.payload);
              await pc.addIceCandidate(new RTCIceCandidate(candidate));
            } catch (err) {
              logger.error('Failed to add ICE candidate:', err);
            }
          }
          break;
        }

        case 'voice-message': {
          try {
            const msg = JSON.parse(message.payload) as IVoiceMessage;
            voiceMessageCallbacksRef.current.forEach((cb) => cb(msg));
          } catch (err) {
            logger.error('Failed to parse voice message:', err);
          }
          break;
        }
      }
    },
    [user?.username, createPeerConnection, sendSignal]
  );

  // Connect WebSocket
  const connectWebSocket = useCallback(() => {
    if (!isAuthenticated) return;

    // Enable and persist preference
    setIsEnabled(true);
    localStorage.setItem('webrtc_enabled', 'true');
    manualDisconnectRef.current = false;

    const wsUrl = ApiService.getWebSocketUrl();
    setConnectionState('connecting');

    try {
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        setConnectionState('connected');
        reconnectAttemptsRef.current = 0;
        logger.log('WebRTC: WebSocket connected');

        // Fetch pending messages on connect
        refreshPendingMessages();
      };

      socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data) as ISignalMessage;
          handleSignalMessage(message);
        } catch (err) {
          logger.error('Failed to parse WebSocket message:', err);
        }
      };

      socket.onclose = () => {
        setConnectionState('disconnected');
        socketRef.current = null;
        logger.log('WebRTC: WebSocket disconnected');

        // Only attempt reconnection if NOT manually disconnected and still enabled
        if (isAuthenticated && !manualDisconnectRef.current) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
          reconnectAttemptsRef.current++;
          setConnectionState('reconnecting');

          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, delay);
        }
      };

      socket.onerror = (error) => {
        logger.error('WebRTC: WebSocket error:', error);
      };
    } catch (error) {
      logger.error('Failed to create WebSocket:', error);
      setConnectionState('disconnected');
    }
  }, [isAuthenticated, handleSignalMessage, refreshPendingMessages]);

  // Disconnect WebSocket
  const disconnectWebSocket = useCallback(() => {
    // Mark as manually disconnected and disable
    manualDisconnectRef.current = true;
    setIsEnabled(false);
    localStorage.setItem('webrtc_enabled', 'false');

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }

    // Close all peer connections
    peerConnectionsRef.current.forEach((pc) => pc.close());
    peerConnectionsRef.current.clear();
    dataChannelsRef.current.clear();

    setConnectionState('disconnected');
    setOnlineUsers([]);
  }, []);

  // Connect/disconnect based on auth state AND enabled state
  useEffect(() => {
    if (isAuthenticated && isEnabled && !socketRef.current) {
      // Only auto-connect if enabled preference is true
      manualDisconnectRef.current = false;
      const wsUrl = ApiService.getWebSocketUrl();
      setConnectionState('connecting');

      try {
        const socket = new WebSocket(wsUrl);
        socketRef.current = socket;

        socket.onopen = () => {
          setConnectionState('connected');
          reconnectAttemptsRef.current = 0;
          logger.log('WebRTC: WebSocket connected (auto)');
          refreshPendingMessages();
        };

        socket.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data) as ISignalMessage;
            handleSignalMessage(message);
          } catch (err) {
            logger.error('Failed to parse WebSocket message:', err);
          }
        };

        socket.onclose = () => {
          setConnectionState('disconnected');
          socketRef.current = null;
          logger.log('WebRTC: WebSocket disconnected');

          // Only attempt reconnection if NOT manually disconnected
          if (isAuthenticated && isEnabled && !manualDisconnectRef.current) {
            const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
            reconnectAttemptsRef.current++;
            setConnectionState('reconnecting');

            reconnectTimeoutRef.current = setTimeout(() => {
              // Trigger re-run of this effect
              setIsEnabled((prev) => prev);
            }, delay);
          }
        };

        socket.onerror = (error) => {
          logger.error('WebRTC: WebSocket error:', error);
        };
      } catch (error) {
        logger.error('Failed to create WebSocket:', error);
        setConnectionState('disconnected');
      }
    } else if (!isAuthenticated) {
      // Clean up when logged out
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      peerConnectionsRef.current.forEach((pc) => pc.close());
      peerConnectionsRef.current.clear();
      dataChannelsRef.current.clear();
      setConnectionState('disconnected');
      setOnlineUsers([]);
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [isAuthenticated, isEnabled, handleSignalMessage, refreshPendingMessages]);

  // Periodically refresh pending messages (only when enabled)
  useEffect(() => {
    if (!isAuthenticated || !isEnabled) return;

    const interval = setInterval(() => {
      refreshPendingMessages();
    }, 30000); // Every 30 seconds

    return () => clearInterval(interval);
  }, [isAuthenticated, isEnabled, refreshPendingMessages]);

  // Send voice message
  const sendVoiceMessage = useCallback(
    async (to: string, audioBlob: Blob, duration: number): Promise<boolean> => {
      // Check if user is online
      const isOnline = onlineUsers.some((u) => u.username === to);

      if (isOnline) {
        // Try P2P delivery via data channel
        let dataChannel = dataChannelsRef.current.get(to);

        if (!dataChannel || dataChannel.readyState !== 'open') {
          // Need to establish connection first
          let pc = peerConnectionsRef.current.get(to);
          if (!pc) {
            pc = createPeerConnection(to);
          }

          // Create data channel
          dataChannel = pc.createDataChannel('voiceMessages');
          dataChannelsRef.current.set(to, dataChannel);

          // Create and send offer
          try {
            const offer = await pc.createOffer();
            await pc.setLocalDescription(offer);
            sendSignal({
              type: 'offer',
              to,
              payload: JSON.stringify(offer),
            });

            // Wait for connection to be established (timeout 5s)
            await new Promise<void>((resolve, reject) => {
              const timeout = setTimeout(() => reject(new Error('Connection timeout')), 5000);
              dataChannel!.onopen = () => {
                clearTimeout(timeout);
                resolve();
              };
            });
          } catch (err) {
            logger.error('Failed to establish P2P connection:', err);
            // Fall back to server-stored message
          }
        }

        // If data channel is open, send via P2P
        if (dataChannel?.readyState === 'open') {
          const base64Audio = await blobToBase64(audioBlob);
          const message: IVoiceMessage = {
            id: crypto.randomUUID(),
            from: user?.username || '',
            from_name: user?.name || '',
            to,
            duration,
            timestamp: Date.now(),
          };

          dataChannel.send(
            JSON.stringify({
              ...message,
              audio_base64: base64Audio,
            })
          );
          return true;
        }
      }

      // User is offline or P2P failed - store on server
      try {
        const base64Audio = await blobToBase64(audioBlob);
        await ApiService.storeVoiceMessage({
          to,
          audio_base64: base64Audio,
          duration,
        });
        return true;
      } catch (error) {
        logger.error('Failed to store voice message:', error);
        return false;
      }
    },
    [onlineUsers, user, createPeerConnection, sendSignal, blobToBase64]
  );

  // Subscribe to voice messages
  const onVoiceMessage = useCallback((callback: (msg: IVoiceMessage) => void) => {
    voiceMessageCallbacksRef.current.add(callback);
    return () => {
      voiceMessageCallbacksRef.current.delete(callback);
    };
  }, []);

  // Delete message
  const deleteMessage = useCallback(async (messageId: string) => {
    await ApiService.deleteVoiceMessage(messageId);
    setPendingMessages((prev) => prev.filter((m) => m.id !== messageId));
    setPendingCount((prev) => Math.max(0, prev - 1));
  }, []);

  // Mark message as played
  const markMessagePlayed = useCallback(async (messageId: string) => {
    await ApiService.markVoiceMessagePlayed(messageId);
    setPendingMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, played: true } : m))
    );
    setUnplayedCount((prev) => Math.max(0, prev - 1));
  }, []);

  const value: WebRTCContextType = {
    isConnected: connectionState === 'connected',
    isEnabled,
    connectionState,
    error,
    onlineUsers,
    pendingMessages,
    pendingCount,
    unplayedCount,
    refreshPendingMessages,
    sendVoiceMessage,
    onVoiceMessage,
    deleteMessage,
    markMessagePlayed,
    connect: connectWebSocket,
    disconnect: disconnectWebSocket,
  };

  return <WebRTCContext.Provider value={value}>{children}</WebRTCContext.Provider>;
};

export const useWebRTC = (): WebRTCContextType => {
  const context = useContext(WebRTCContext);
  if (context === undefined) {
    throw new Error('useWebRTC must be used within a WebRTCProvider');
  }
  return context;
};
